/* eslint-disable @typescript-eslint/naming-convention */
/**
 * `@typescript-eslint/naming-convention` is overridden in this file as this is
 * duplicated/imported code from chrome-integrations repo, where some generic
 * names begin with letters other than `T`.
 */
import { analytics } from 'app/utils/segment';
import { removeAnchorFromArticleId, removeAnchorFromMultipleArticleIds, } from '../utils';
import { trackSelfDescribingEvent } from './snowcat';
/**
 * This is triggered when the completion workflow begins.
 *
 * @param ticketId
 */
export function ticketResolutionStart({ ticketId, url, languageId, }) {
    trackSelfDescribingEvent('iglu:com.elevio/ticket_resolution_start/jsonschema/1-0-0', {
        ticket_id: ticketId,
    }, url, languageId);
    analytics.knowledgeResolutionInitialised({
        conversationId: ticketId,
        knowledgeSource: 'elevio',
    });
}
/**
 * When a ticket is resolved this event is fired, so this happens before the
 * final confirmation modal is shown.
 *
 * @param helpfulArticleId
 * @param ticketId
 * @param ticketThread
 * @param suggestedArticleIds
 * @param ticketTags
 */
export function ticketResolved({ helpfulArticleId, ticketId, ticketThread, suggestedArticleIds, ticketTags, ticketSubject, url, languageId, }) {
    const suggested_article_ids = removeAnchorFromMultipleArticleIds(suggestedArticleIds);
    const helpful_article_ids = removeAnchorFromMultipleArticleIds([
        helpfulArticleId,
    ]);
    trackSelfDescribingEvent('iglu:com.elevio/ticket_resolution/jsonschema/1-0-2', {
        ticket_id: ticketId,
        ticket_thread: ticketThread,
        ticket_subject: (ticketSubject || '').substring(0, 499),
        ticket_tags: ticketTags,
        suggested_article_ids,
        helpful_article_ids,
    }, url, languageId);
    analytics.conversationResolvedByKnowledge({
        conversationId: ticketId,
        helpfulArticleIds: helpful_article_ids,
        knowledgeSource: 'elevio',
        suggestedArticleIds: suggested_article_ids,
    });
}
/**
 * TicketResolutionExit is fired when the completion modal is closed without
 * completing the flow. ie, the 'X' modal close button is pressed.
 *
 * @param ticketId
 * @param url
 */
export function ticketResolutionExit({ ticketId, url, languageId, }) {
    trackSelfDescribingEvent('iglu:com.elevio/ticket_resolution_exit/jsonschema/1-0-0', {
        ticket_id: ticketId,
    }, url, languageId);
    analytics.knowledgeResolutionCancelled({
        conversationId: ticketId,
        knowledgeSource: 'elevio',
    });
}
export function ticketNonResolution({ ticketId, ticketThread, suggestedArticleIds, ticketTags, ticketSubject, url, languageId, }) {
    const suggested_article_ids = removeAnchorFromMultipleArticleIds(suggestedArticleIds);
    trackSelfDescribingEvent('iglu:com.elevio/ticket_non_resolution/jsonschema/1-0-2', {
        ticket_id: ticketId,
        ticket_thread: ticketThread,
        ticket_subject: (ticketSubject || '').substring(0, 499),
        ticket_tags: ticketTags,
        suggested_article_ids,
    }, url, languageId);
    analytics.conversationResolvedWithNoKnowledgeArticle({
        knowledgeSource: 'elevio',
        conversationId: ticketId,
        suggestedArticleIds: suggested_article_ids,
    });
}
/**
 * This is send we a user click on "This article helped" or they copy text from
 * an article.
 *
 * @param helpfulArticleId
 * @param ticketId
 * @param ticketThread
 * @param suggestedArticleIds
 * @param ticketTags
 * @param ticketTags
 * @param assistType
 */
export function ticketAssist({ helpfulArticleIds, ticketId, ticketThread, suggestedArticleIds, ticketTags, assistType, ticketSubject, url, languageId, }) {
    const suggested_article_ids = removeAnchorFromMultipleArticleIds(suggestedArticleIds);
    const helpful_article_ids = removeAnchorFromMultipleArticleIds(helpfulArticleIds);
    trackSelfDescribingEvent('iglu:com.elevio/ticket_assist/jsonschema/1-0-2', {
        ticket_id: ticketId,
        ticket_thread: ticketThread,
        ticket_subject: (ticketSubject || '').substring(0, 499),
        ticket_tags: ticketTags,
        suggested_article_ids,
        helpful_article_ids,
        assist_type: assistType,
    }, url, languageId);
    // When a user chooses an article that helped to resolve a conversation
    analytics.knowledgeArticleMarkedAsHelpful({
        knowledgeSource: 'elevio',
        conversationId: ticketId,
        suggestedArticleIds: suggested_article_ids,
        helpfulArticleIds: helpful_article_ids,
        assistType, // copy|helpful_click
    });
}
export function articleView({ articleId, ticketTags, ticketStatus, url, ticketId, languageId, }) {
    const article_id = removeAnchorFromArticleId(articleId);
    trackSelfDescribingEvent('iglu:com.elevio/article_view/jsonschema/1-0-1', {
        ticket_id: ticketId,
        ticket_tags: ticketTags,
        ticket_status: ticketStatus || 'unknown',
        article_id,
    }, url, languageId);
    analytics.knowledgeArticleViewed({
        knowledgeSource: 'elevio',
        articleId,
        conversationId: ticketId,
        source: 'conversation-ui',
    });
}
export function articleFeedback({ articleId, articleLastPublishedAt, ticketId, feedbackText, url, languageId, }) {
    const article_id = removeAnchorFromArticleId(articleId);
    trackSelfDescribingEvent('iglu:com.elevio/article_feedback/jsonschema/1-0-1', {
        ticket_id: ticketId,
        article_last_published_at: articleLastPublishedAt,
        article_id,
        feedback_text: feedbackText,
    }, url, languageId);
    analytics.knowledgeArticleFeedbackSubmitted({
        articleId,
        articleLastPublishedAt,
        conversationId: ticketId,
        knowledgeSource: 'elevio',
        source: 'conversation-ui',
    });
}
export function articleDraft({ ticketId, ticketTitle, ticketThread, url, languageId, }) {
    trackSelfDescribingEvent('iglu:com.elevio/article_draft/jsonschema/1-0-0', {
        ticket_id: ticketId,
        ticket_title: ticketTitle || 'Untitled Ticket',
        ticket_thread: ticketThread,
    }, url, languageId);
    analytics.conversationResolvedAsAKnowledgeArticleDraft({
        conversationId: ticketId,
        knowledgeSource: 'elevio',
    });
}
export function knowledgeGap({ ticketId, ticketTags, suggestedArticleIds, url, languageId, }) {
    const suggested_article_ids = removeAnchorFromMultipleArticleIds(suggestedArticleIds);
    trackSelfDescribingEvent('iglu:com.elevio/knowledge_gap/jsonschema/1-0-1', {
        ticket_id: ticketId,
        ticket_tags: ticketTags,
        suggested_article_ids,
    }, url, languageId);
    analytics.conversationResolvedAsAKnowledgeGap({
        conversationId: ticketId,
        suggestedArticleIds: suggested_article_ids,
        knowledgeSource: 'elevio',
    });
}
export function ticketView({ ticketId, status, url, languageId, }) {
    trackSelfDescribingEvent('iglu:com.elevio/ticket_view/jsonschema/1-0-0', {
        ticket_id: ticketId,
        ticket_status: status,
    }, url, languageId);
    // No `analytics` event needed here as it is already tracked elsewhere
}
